import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconCheck, IconDeviceMobile, IconMail } from "@tabler/icons-react";

import { Button, Input, Text } from "@fragments";

import { CHANNELS } from "../../../hooks/useVerificationFlow";
import FlowMessage, { flowMessageText } from "../FlowMessage";

function formatCountdown(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Type the six digits. Submits on its own when the last square fills; the
 * status line below carries attempts left / expiry / errors, and the actions
 * row holds resend (with its countdown) and "try another way".
 *
 * Screen readers hear ONE visually-hidden `role="status"` element whose text
 * changes only per event (code sent, wrong code, expired, rate-limited,
 * verified). The visible status line is deliberately not live: its idle
 * content is the expiry countdown, which re-renders every second and would be
 * re-announced on every tick.
 */
export default function CodeStep({
    channel,
    target,
    code,
    codeStatus,
    message,
    sending,
    verifying,
    resendIn,
    expiresIn,
    canResend,
    codeInputRef,
    onCodeChange,
    onCodeComplete,
    onResend,
    onChooseAnother,
}) {
    const { t } = useTranslation("common");
    // The visible status line describes the squares (wrong code, attempts
    // left, expiry) — read on focus, not announced on change.
    const statusId = useId();

    const success = codeStatus === "success";
    const TargetIcon = channel === CHANNELS.SMS ? IconDeviceMobile : IconMail;

    // ── Announcer ───────────────────────────────────────────────────────
    // Values an announcement may quote but must not react to (the countdowns
    // tick every second): read through a ref so they stay out of the effect's
    // dependencies.
    const [announcement, setAnnouncement] = useState("");
    const snapshot = useRef({ target, resendIn, expiresIn });
    snapshot.current = { target, resendIn, expiresIn };
    // "Code sent" is said once per send: the flag drops when a send starts and
    // is spent by the first announcement after it (the sent line itself, or an
    // error that superseded it).
    const sendAnnounced = useRef(false);

    useEffect(() => {
        const { target: to, resendIn: seconds, expiresIn: ttl } = snapshot.current;

        if (sending) sendAnnounced.current = false;

        if (message) {
            if (!sending) sendAnnounced.current = true;
            setAnnouncement(flowMessageText(t, message, seconds) || "");
            return;
        }

        if (sending) return;

        if (success) {
            setAnnouncement(t("verification.code.verified"));
            return;
        }

        if (!sendAnnounced.current && typeof ttl === "number") {
            sendAnnounced.current = true;
            setAnnouncement(
                to
                    ? t("verification.code.subtitle", { target: to })
                    : t("verification.code.title"),
            );
        }
    }, [message, success, sending, t]);

    let status = null;
    if (message) {
        status = <FlowMessage message={message} resendIn={resendIn} />;
    } else if (success) {
        status = (
            <Text.caption contrast={6} left={<IconCheck />}>
                {t("verification.code.verified")}
            </Text.caption>
        );
    } else if (verifying) {
        status = <Text.caption contrast={6}>{t("verification.code.verifying")}</Text.caption>;
    } else if (sending) {
        // Auto-request: the step opened before the code left the server.
        status = <Text.caption contrast={6}>{t("verification.code.sending")}</Text.caption>;
    } else if (typeof expiresIn === "number") {
        status = (
            <Text.caption contrast={6}>
                {t("verification.code.expiresIn", { time: formatCountdown(expiresIn) })}
            </Text.caption>
        );
    }

    return (
        <div className="verification-code">
            {target && (
                <div className="verification-code__target">
                    <span className="verification-code__target-icon">
                        <TargetIcon />
                    </span>
                    <Text.span contrast={7}>{target}</Text.span>
                </div>
            )}

            <div className="verification-code__input">
                <Input.Code
                    ref={codeInputRef}
                    value={code}
                    onChange={onCodeChange}
                    onComplete={onCodeComplete}
                    status={codeStatus}
                    disabled={verifying || sending}
                    autoFocus
                    aria-label={t("verification.code.inputLabel")}
                    aria-describedby={statusId}
                />
            </div>

            <div id={statusId} className="verification-code__status">
                {status}
            </div>

            <div className="verification-code__announcer" role="status">
                {announcement}
            </div>

            <div className="verification-code__actions">
                <Button
                    variant="text"
                    color="overlay"
                    size="small"
                    disabled={!canResend}
                    onClick={onResend}
                >
                    {resendIn > 0
                        ? t("verification.code.resendIn", { seconds: resendIn })
                        : t("verification.code.resend")}
                </Button>
                <Button
                    variant="text"
                    color="overlay"
                    size="small"
                    disabled={success || verifying}
                    preventLoading
                    onClick={onChooseAnother}
                >
                    {t("verification.code.tryAnother")}
                </Button>
            </div>
        </div>
    );
}
